import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSwiper } from 'swiper/react';
import { ArrowBack, ArrowForward } from '@one-thd/sui-icons';
import { Fab, IconButton } from '@one-thd/sui-atomic-components';

const BUTTON_SPACING_OPTIONS = {
  '-4': {
    left: '-sui-left-4',
    right: '-sui-right-4'
  },
  '-2': {
    left: '-sui-left-2',
    right: '-sui-right-2'
  },
  '-1': {
    left: '-sui-left-1',
    right: '-sui-right-1'
  },
  0: {
    left: 'sui-left-0',
    right: 'sui-right-0'
  },
  1: {
    left: 'sui-left-1',
    right: 'sui-right-1'
  },
  2: {
    left: 'sui-left-2',
    right: 'sui-right-2'
  },
  4: {
    left: 'sui-left-4',
    right: 'sui-right-4'
  },
  6: {
    left: 'sui-left-6',
    right: 'sui-right-6'
  }
};

const getSwiperPaginationData = (swiper) => {
  if (!swiper || swiper.destroyed) {
    return { current: 0, total: 0 };
  }
  // See: https://github.com/nolimits4web/swiper/blob/master/src/modules/pagination/pagination.js#L70
  const slidesLength = swiper.virtual && swiper.params.virtual.enabled
    ? swiper.virtual.slides.length
    : swiper.slides.length;
  // Current/Total
  let current;
  const total = swiper.params.loop
    ? Math.ceil((slidesLength - swiper.loopedSlides * 2) / swiper.params.slidesPerGroup)
    : swiper.snapGrid.length;
  if (swiper.params.loop) {
    current = Math.ceil(
      (swiper.activeIndex - swiper.loopedSlides) / swiper.params.slidesPerGroup,
    );
    if (current > slidesLength - 1 - swiper.loopedSlides * 2) {
      current -= slidesLength - swiper.loopedSlides * 2;
    }
    if (current > total - 1) current -= total;
    if (current < 0 && swiper.params.paginationType !== 'bullets') current = total + current;
  } else if (typeof swiper.snapIndex !== 'undefined') {
    current = swiper.snapIndex;
  } else {
    current = swiper.activeIndex || 0;
  }
  return { current: current + 1, total };
};

const Paginator = React.forwardRef((props, ref) => {
  const {
    buttonSpacing = '4',
    loop
  } = props;

  const swiper = useSwiper();
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const updateBoundaries = (updatedSwiper) => {
    setIsBeginning(updatedSwiper.isBeginning);
    setIsEnd(updatedSwiper.isEnd);
    const paginationData = getSwiperPaginationData(updatedSwiper);
    setCurrent(paginationData.current);
    setTotal(paginationData.total);
  };

  const resetCurrentIndex = (updatedSwiper) => {
    updatedSwiper.slideTo(0);
  };

  const isRendered = !(current === 1 && total === 1);

  useEffect(() => {
    swiper.on('progress', updateBoundaries);
    swiper.on('activeIndexChange', updateBoundaries);
    swiper.on('update', resetCurrentIndex);
    updateBoundaries(swiper);
    return () => {
      swiper.off('progress', updateBoundaries);
      swiper.off('activeIndexChange', updateBoundaries);
      swiper.off('update', resetCurrentIndex);
    };
  }, [swiper]);

  const leftButtonClasses = `sui-absolute sui-top-[40%] sui-flex sui-items-center -sui-m-1 ${BUTTON_SPACING_OPTIONS[buttonSpacing].left} sui-z-tooltip`;
  const rightButtonClasses = `sui-absolute sui-top-[40%] sui-flex sui-items-center -sui-m-1 ${BUTTON_SPACING_OPTIONS[buttonSpacing].right} sui-z-tooltip`;

  const leftContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete]', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100 supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0': !isBeginning || loop,
  }, leftButtonClasses);
  const shadowLeftContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', leftButtonClasses);
  const rightContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete]', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100 supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0': !isEnd || loop,
  }, rightButtonClasses);
  const shadowRightContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', rightButtonClasses);

  return isRendered && (
    <>
      <div className={leftContainerClasses}>
        <Fab
          disabled={isBeginning && !loop}
          aria-label={isBeginning ? 'This is the first slide' : 'Previous slide'}
          onClick={() => swiper.slidePrev()}
          tabIndex={-1}
        >
          <ArrowBack size="small" />
        </Fab>
      </div>
      <div className={shadowLeftContainerClasses}>
        <Fab
          disabled={isBeginning && !loop}
          aria-label={isBeginning ? 'This is the first slide' : 'Previous slide'}
          onClick={() => swiper.slidePrev()}
        >
          <ArrowBack size="small" />
        </Fab>
      </div>
      <div className={rightContainerClasses}>
        <Fab
          ref={ref}
          disabled={isEnd && !loop}
          aria-label={isEnd ? 'This is the last slide' : 'Next slide'}
          onClick={() => swiper.slideNext()}
          tabIndex={-1}
        >
          <ArrowForward size="small" />
        </Fab>
      </div>
      <div className={shadowRightContainerClasses}>
        <Fab
          disabled={isEnd && !loop}
          aria-label={isEnd ? 'This is the last slide' : 'Next slide'}
          onClick={() => swiper.slideNext()}
        >
          <ArrowForward size="small" />
        </Fab>
      </div>
    </>
  );
});

export { Paginator, getSwiperPaginationData };
